import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api, money, phoneFmt, todayIso } from '../api';
import { useApi } from '../useApi';
import { Empty, ErrorBox, Loading, Status, useToast } from '../ui';

type RouteRow = {
  id: string;
  date: string;
  status: string;
  vehicle: string | null;
  loadedFull: number;
  returnedEmpty: number;
  driver: { id: string; name: string; phone: string } | null;
  stops: Array<{
    id: string;
    position: number;
    doneAt: string | null;
    plannedGive: number;
    plannedTake: number;
    order: {
      id: string;
      number: string;
      status: string;
      totalKop: string;
      client: { id: string; name: string; phone: string; cachedBalanceKop: string };
      address: { street: string; house: string; flat: string | null; comment: string | null };
      delivery: { givenFull: number; takenEmpty: number; paidKop: string; status: string } | null;
    };
  }>;
};

type Staff = { id: string; name: string; role: string; active: boolean };

export function RoutesPage() {
  const [date, setDate] = useState(todayIso());
  const { data, error, loading, reload } = useApi<RouteRow[]>(`/routes?date=${date}`);
  const staff = useApi<Staff[]>('/users');
  const toast = useToast();

  /**
   * Назначение водителя уже собранному маршруту.
   *
   * На «Заказах» маршрут можно собрать с «назначу позже», и обещание надо где-то
   * выполнять: доска на черновике прямо посылает оператора сюда. Пока этого поля
   * не было, такой маршрут до вечера оставался без машины.
   */
  const assign = async (r: RouteRow, driverId: string) => {
    await api(`/routes/${r.id}`, { method: 'PATCH', body: { driverId: driverId || null } });
    toast.show(driverId ? 'Водитель назначен' : 'Водитель снят');
    await reload();
  };

  const close = async (r: RouteRow) => {
    const empty = prompt('Сколько пустой тары вернули на склад?');
    if (empty === null) return;
    const res = await api<{ reconciliation: Record<string, number> }>(`/routes/${r.id}/close`, {
      body: { returnedEmpty: Number(empty) },
    });
    const rec = res.reconciliation;
    toast.show(
      `Маршрут закрыт. Полной осталось ${rec.fullLeftInCar}, расхождение по пустой: ${rec.emptyMismatch}`,
      6000,
    );
    await reload();
  };

  return (
    <>
      <div className="page-head">
        <h1>Маршруты</h1>
        <Link className="btn" to={`/board?date=${date}`}>
          Собрать маршрут на доске →
        </Link>
      </div>

      <div className="card">
        <div className="field" style={{ maxWidth: 200, marginBottom: 0 }}>
          <label>Дата</label>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </div>
      </div>

      {loading ? (
        <Loading />
      ) : error ? (
        <ErrorBox error={error} />
      ) : !data?.length ? (
        <Empty>
          На эту дату маршрутов нет.
          <div style={{ marginTop: 10 }}>
            <Link className="btn primary" to={`/board?date=${date}`}>
              Собрать маршрут
            </Link>
          </div>
        </Empty>
      ) : (
        data.map((r) => {
          const done = r.stops.filter((s) => s.doneAt).length;
          const given = r.stops.reduce((s, x) => s + (x.order.delivery?.givenFull ?? 0), 0);
          const taken = r.stops.reduce((s, x) => s + (x.order.delivery?.takenEmpty ?? 0), 0);
          const collected = r.stops.reduce((s, x) => s + BigInt(x.order.delivery?.paidKop ?? '0'), 0n);
          return (
            <div className="card" key={r.id}>
              <div className="spread">
                <h2 style={{ margin: 0 }}>
                  {r.driver?.name ?? 'Без водителя'}{' '}
                  <span className="muted" style={{ fontWeight: 400 }}>
                    {r.vehicle ?? ''} · {phoneFmt(r.driver?.phone)}
                  </span>
                </h2>
                <div className="row">
                  <Status value={r.status} />
                  {r.status === 'DRAFT' || r.status === 'PLANNED' ? (
                    <select
                      data-driver={r.id}
                      style={{ width: 200 }}
                      value={r.driver?.id ?? ''}
                      onChange={(e) => void assign(r, e.target.value)}
                    >
                      <option value="">Водитель не назначен</option>
                      {(staff.data ?? [])
                        .filter((u) => u.role === 'DRIVER' && u.active)
                        .map((d) => (
                          <option key={d.id} value={d.id}>
                            {d.name}
                          </option>
                        ))}
                    </select>
                  ) : null}
                  {r.status === 'PLANNED' ? (
                    <button onClick={async () => { await api(`/routes/${r.id}/start`, { body: {} }); await reload(); }}>
                      Выпустить в рейс
                    </button>
                  ) : null}
                  {r.status === 'ACTIVE' ? (
                    <button className="primary" onClick={() => void close(r)}>
                      Закрыть маршрут
                    </button>
                  ) : null}
                </div>
              </div>

              <div className="row muted" style={{ margin: '8px 0 12px', gap: 20 }}>
                <span>Точек: {done} / {r.stops.length}</span>
                <span>Загружено полной: {r.loadedFull}</span>
                <span>Выдано: {given}</span>
                <span>Забрано пустой: {taken}</span>
                <span>Собрано денег: {money(collected)}</span>
                {r.status === 'CLOSED' ? <span>Сдано пустой: {r.returnedEmpty}</span> : null}
              </div>

              <table>
                <thead>
                  <tr>
                    <th style={{ width: 30 }}>#</th>
                    <th>Клиент</th>
                    <th>Адрес</th>
                    <th className="num">План +/−</th>
                    <th className="num">Факт +/−</th>
                    <th className="num">Сумма</th>
                    <th>Состояние</th>
                  </tr>
                </thead>
                <tbody>
                  {r.stops.map((s) => (
                    <tr key={s.id}>
                      <td className="mono">{s.position}</td>
                      <td>
                        <Link to={`/clients/${s.order.client.id}`}>{s.order.client.name}</Link>
                        {BigInt(s.order.client.cachedBalanceKop) < 0n ? (
                          <div>
                            <span className="badge red">долг {money(-BigInt(s.order.client.cachedBalanceKop))}</span>
                          </div>
                        ) : null}
                      </td>
                      <td>
                        {s.order.address.street}, {s.order.address.house}
                        {s.order.address.flat ? `, ${s.order.address.flat}` : ''}
                        {s.order.address.comment ? (
                          <div className="muted" style={{ fontSize: 12 }}>{s.order.address.comment}</div>
                        ) : null}
                      </td>
                      <td className="num">
                        {s.plannedGive} / {s.plannedTake}
                      </td>
                      <td className="num">
                        {s.order.delivery ? `${s.order.delivery.givenFull} / ${s.order.delivery.takenEmpty}` : '—'}
                      </td>
                      <td className="num">{money(s.order.totalKop)}</td>
                      <td>
                        <Status value={s.order.status} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        })
      )}
      {toast.node}
    </>
  );
}
